<template>
  <div class="chat-view" @click="markAsRead" tabindex="0" @focus="markAsRead">
    <div class="chat-header">
      <div class="chat-info">
        <div class="avatar">
          <div v-if="!chat.photoUrl" class="avatar-placeholder">
            {{ getInitials() }}
          </div>
          <img v-else :src="chat.photoUrl" alt="Avatar" class="avatar-image">
        </div>
        <div class="chat-details">
          <div class="chat-name">{{ chatName }}</div>
          <div class="chat-meta">
            <div v-if="chat.status" class="chat-status-badge" :style="{ backgroundColor: chat.status.color + '20', color: chat.status.color }">
              {{ chat.status.name }}
            </div>
          </div>
        </div>
      </div>
      <div class="chat-actions">
        <div class="status-dropdown" v-click-outside="closeStatusDropdown">
          <button class="status-button" @click="toggleStatusDropdown" :style="{ backgroundColor: statusColor }">
            {{ statusName }}
            <i class="fas fa-chevron-down"></i>
          </button>
          <div class="status-dropdown-content" v-if="statusDropdownOpen">
            <div 
              v-for="status in statuses" 
              :key="status._id" 
              class="status-item"
              :style="{ borderLeftColor: status.color }"
              @click="changeStatus(status._id)"
            >
              {{ status.name }}
            </div>
          </div>
        </div>
        
        <button 
          @click="assignToMe" 
          class="assign-button" 
          :class="{ 'assigned': isAssignedToMe, 'assigned-other': isAssignedToOther }"
          :disabled="isAssignedToOther"
          :title="assignButtonTitle"
        >
          <i class="fas" :class="assignButtonIcon"></i>
          {{ assignButtonText }}
        </button>
      </div>
    </div>
    
    <div class="messages-container" ref="messagesContainer">
      <div v-if="loading" class="loading-messages">
        <i class="fas fa-spinner fa-spin"></i> Загрузка сообщений...
      </div>
      
      <div v-else-if="error" class="chat-error-message">
        {{ error }}
      </div>
      
      <div v-else-if="messages.length === 0" class="no-messages">
        Нет сообщений
      </div>
      
      <div v-else class="messages">
        <template v-for="(message, index) in messagesWithDateSeparators" :key="message._id || message.separator">
          <!-- Разделитель даты -->
          <div v-if="message.separator" class="date-separator">
            <div class="date-separator-line"></div>
            <div class="date-separator-text">{{ message.date }}</div>
            <div class="date-separator-line"></div>
          </div>
          
          <!-- Обычное сообщение -->
          <div 
            v-else
            class="message" 
            :class="{ 'from-me': message.fromMe, 'media-message': isMediaMessage(message) }"
          >
            <div class="message-content">
              <div class="message-text" v-html="getMessageContent(message)"></div>
              <div class="message-time">{{ formatTime(message.date) }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>
    
    <div class="message-input-container">
      <textarea 
        v-model="newMessage" 
        class="message-input" 
        placeholder="Введите сообщение..." 
        @keydown.enter.prevent="sendMessage"
        :disabled="!canSendMessages"
        @focus="markAsRead"
      ></textarea>
      <button 
        class="send-button" 
        @click="sendMessage" 
        :disabled="sending || !newMessage.trim() || !canSendMessages"
        :title="sendButtonTitle"
      >
        <i class="fas fa-paper-plane"></i>
      </button>
    </div>
    
    <div v-if="!canSendMessages && chat.assignedTo" class="chat-locked-message">
      <i class="fas fa-lock"></i> Этот чат назначен пользователю <strong>{{ chat.assignedTo.name }}</strong>. 
      Вы не можете отправлять сообщения.
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import socket, { subscribeToChat, unsubscribeFromChat } from '../plugins/socket';

// Директива для закрытия выпадающего списка при клике вне элемента
const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('click', el._clickOutside);
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside);
  }
};

// Добавляем переменную для режима разработки
const isDevelopment = process.env.NODE_ENV === 'development';

export default {
  name: 'ChatView',
  directives: {
    'click-outside': vClickOutside
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const chatId = ref(props.id);
    
    // Используем вычисляемое свойство для получения чата из хранилища
    const currentChat = computed(() => {
      // Сначала проверяем, есть ли чат в хранилище
      const chatFromStore = store.state.chats.find(c => c._id === chatId.value);
      if (chatFromStore) {
        return chatFromStore;
      }
      // Если нет, возвращаем локальное состояние
      return store.state.currentChat || {};
    });
    
    const messages = ref([]);
    const newMessage = ref('');
    const sending = ref(false);
    const loading = ref(true);
    const error = ref(null);
    const messagesContainer = ref(null);
    const statuses = ref([]);
    const statusDropdownOpen = ref(false);
    
    // Добавляем переменные для пагинации
    const page = ref(1);
    const limit = ref(25);
    const hasMoreMessages = ref(true);
    const loadingMore = ref(false);
    
    // Интервал для периодического обновления сообщений (как запасной вариант)
    let messageInterval = null;
    
    const chatName = computed(() => {
      if (currentChat.value.firstName || currentChat.value.lastName) {
        return `${currentChat.value.firstName || ''} ${currentChat.value.lastName || ''}`.trim();
      }
      return currentChat.value.username ? `@${currentChat.value.username}` : currentChat.value.telegramId;
    });
    
    const statusName = computed(() => {
      return currentChat.value.status ? currentChat.value.status.name : 'Без статуса';
    });
    
    const statusColor = computed(() => {
      return currentChat.value.status ? currentChat.value.status.color : '#cccccc';
    });
    
    const isAssignedToMe = computed(() => {
      if (!currentChat.value.assignedTo || !store.state.auth.user) return false;
      return currentChat.value.assignedTo._id === store.state.auth.user.id;
    });
    
    const isAssignedToOther = computed(() => {
      if (!currentChat.value.assignedTo || !store.state.auth.user) return false;
      return currentChat.value.assignedTo._id !== store.state.auth.user.id;
    });
    
    const assignButtonText = computed(() => {
      if (isAssignedToMe.value) return 'Назначено мне';
      if (isAssignedToOther.value) {
        const assignedName = `${currentChat.value.assignedTo.name || ''}`.trim();
        return `Назначено: ${assignedName}`;
      }
      return 'Взять на себя';
    });
    
    const assignButtonIcon = computed(() => {
      if (isAssignedToMe.value) return 'fa-user-check';
      if (isAssignedToOther.value) return 'fa-user-lock';
      return 'fa-user-plus';
    });
    
    const assignButtonTitle = computed(() => {
      if (isAssignedToOther.value) {
        return 'Этот чат уже назначен другому пользователю';
      }
      return '';
    });
    
    const getInitials = () => {
      if (currentChat.value.firstName && currentChat.value.firstName.length > 0) {
        return currentChat.value.firstName.charAt(0).toUpperCase();
      }
      if (currentChat.value.username && currentChat.value.username.length > 0) {
        return currentChat.value.username.charAt(0).toUpperCase();
      }
      return '?';
    };
    
    const formatTime = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    const formatLastSeen = (timestamp) => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) {
        return 'только что';
      } else if (diffMins < 60) {
        return `${diffMins} мин. назад`;
      } else if (diffMins < 1440) {
        const hours = Math.floor(diffMins / 60);
        return `${hours} ч. назад`;
      } else {
        return date.toLocaleDateString();
      }
    };
    
    const fetchChat = async () => {
      try {
        const response = await axios.get(`/telegram/chats/${chatId.value}`);
        store.dispatch('updateChat', response.data);
        store.commit('setCurrentChat', response.data);
      } catch (err) {
        console.error('Ошибка при получении информации о чате:', err);
        error.value = 'Не удалось загрузить информацию о чате';
      }
    };
    
    const fetchMessages = async (loadMore = false) => {
      try {
        if (!loadMore) {
          loading.value = true;
          page.value = 1;
        } else {
          loadingMore.value = true;
        }
        
        error.value = null;
        
        console.log(`Загрузка сообщений для чата ${chatId.value}, страница ${page.value}`);
        const response = await axios.get(`/telegram/chats/${chatId.value}/messages`, {
          params: {
            page: page.value,
            limit: limit.value
          }
        });
        
        // Обрабатываем новый формат ответа с сервера
        const { messages: newMessages, hasMore } = response.data;
        
        if (loadMore) {
          // Добавляем новые сообщения в начало списка
          messages.value = [...newMessages, ...messages.value];
        } else {
          messages.value = newMessages;
          // Прокручиваем к последнему сообщению только при первой загрузке
          nextTick(() => {
            scrollToBottom();
          });
        }
        
        // Обновляем флаг наличия дополнительных сообщений
        hasMoreMessages.value = hasMore;
        
        loading.value = false;
        loadingMore.value = false;
      } catch (err) {
        console.error('Ошибка при загрузке сообщений:', err);
        
        // Проверяем, является ли ошибка ошибкой доступа
        if (err.response && err.response.status === 403) {
          error.value = err.response.data.error || 'У вас нет доступа к этому чату';
        } else {
          error.value = 'Не удалось загрузить сообщения';
        }
        
        loading.value = false;
        loadingMore.value = false;
      }
    };
    
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        console.log('Прокрутка к последнему сообщению');
        nextTick(() => {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        });
      }
    };
    
    // Проверяем, может ли пользователь отправлять сообщения в этот чат
    const canSendMessages = computed(() => {
      // Если пользователь администратор, он может писать в любой чат
      if (store.getters.isAdmin) return true;
      
      // Если чат не назначен никому, любой пользователь может писать
      if (!currentChat.value.assignedTo) return true;
      
      // Если чат назначен текущему пользователю, он может писать
      return isAssignedToMe.value;
    });
    
    const sendButtonTitle = computed(() => {
      if (!canSendMessages.value) {
        return 'Этот чат назначен другому пользователю. Вы не можете отправлять сообщения.';
      }
      return '';
    });
    
    const sendMessage = async () => {
      if (!newMessage.value.trim() || sending.value || !canSendMessages.value) return;
      
      sending.value = true;
      const messageText = newMessage.value.trim();
      
      // Очищаем поле ввода сразу
      newMessage.value = '';
      
      try {
        console.log(`Отправка сообщения в чат ${chatId.value}`);
        
        // Добавляем временное сообщение в список
        const tempMessage = {
          _id: `temp-${Date.now()}`,
          text: messageText,
          date: new Date(),
          fromMe: true,
          isTemp: true,
          chat: chatId.value
        };
        
        messages.value.push(tempMessage);
        
        // Прокручиваем к временному сообщению
        nextTick(() => {
          scrollToBottom();
        });
        
        // Отправляем сообщение на сервер
        const response = await axios.post(`/telegram/chats/${chatId.value}/messages`, {
          text: messageText
        });
        
        // Проверяем, что ответ содержит нужные данные
        if (!response.data || !response.data.message) {
          throw new Error('Некорректный ответ от сервера');
        }
        
        // Удаляем временное сообщение и добавляем реальное
        const realMessage = response.data.message;
        
        // Заменяем временное сообщение на реальное
        const tempIndex = messages.value.findIndex(m => m.isTemp && m.text === messageText);
        if (tempIndex !== -1) {
          messages.value.splice(tempIndex, 1);
        }
        
        // Проверяем, не добавлено ли уже это сообщение через WebSocket
        const isDuplicate = messages.value.some(m => m._id === realMessage._id);
        if (!isDuplicate) {
          messages.value.push(realMessage);
          
          // Прокручиваем к новому сообщению
          nextTick(() => {
            scrollToBottom();
          });
        }
        
        // Обновляем информацию о чате
        if (response.data.chat) {
          store.dispatch('updateChatSilently', response.data.chat);
        }
      } catch (error) {
        console.error('Ошибка при отправке сообщения:', error);
        
        // Показываем ошибку пользователю
        const errorMessage = {
          _id: `error-${Date.now()}`,
          text: 'Ошибка при отправке сообщения. Пожалуйста, попробуйте еще раз.',
          date: new Date(),
          isError: true,
          chat: chatId.value
        };
        
        messages.value.push(errorMessage);
        
        // Возвращаем текст сообщения в поле ввода
        newMessage.value = messageText;
      } finally {
        sending.value = false;
      }
    };
    
    const fetchStatuses = async () => {
      try {
        const response = await axios.get('/statuses');
        statuses.value = response.data;
      } catch (err) {
        console.error('Ошибка при получении статусов:', err);
      }
    };
    
    const toggleStatusDropdown = () => {
      console.log('Переключение выпадающего списка статусов');
      statusDropdownOpen.value = !statusDropdownOpen.value;
      
      // Если открываем список, загружаем статусы
      if (statusDropdownOpen.value) {
        fetchStatuses();
      }
    };
    
    const closeStatusDropdown = () => {
      statusDropdownOpen.value = false;
    };
    
    const changeStatus = async (statusId) => {
      try {
        console.log(`Изменение статуса чата ${chatId.value} на ${statusId || 'нет'}`);
        
        // Отправляем запрос на изменение статуса
        const response = await axios.post(`/telegram/chats/${chatId.value}/status`, {
          statusId
        });
        
        console.log('Ответ сервера на изменение статуса:', response.data);
        
        // Обновляем чат в хранилище
        store.dispatch('updateChat', response.data);
        
        // Устанавливаем текущий чат
        store.commit('setCurrentChat', response.data);
        
        // Запрашиваем обновление списка чатов
        socket.emit('request_chats_update');
        
        // Закрываем выпадающий список статусов
        statusDropdownOpen.value = false;
      } catch (err) {
        console.error('Ошибка при изменении статуса чата:', err);
        
        if (err.response && err.response.data && err.response.data.error) {
          alert(`Ошибка: ${err.response.data.error}`);
        } else {
          alert('Не удалось изменить статус чата');
        }
      }
    };
    
    const assignToMe = async () => {
      try {
        // Если назначено другому, ничего не делаем (кнопка должна быть отключена)
        if (isAssignedToOther.value) {
          console.log('Чат назначен другому пользователю, нельзя назначить себе');
          return;
        }
        
        // Если уже назначено мне, снимаем назначение
        const userId = isAssignedToMe.value ? null : store.state.auth.user.id;
        console.log(userId, store.state);
        
        console.log(`Назначение чата ${chatId.value} ${userId ? 'себе' : 'никому'}`);
        
        // Отправляем запрос на назначение чата
        const response = await axios.post(`/telegram/chats/${chatId.value}/assign`, {
          userId
        });
        
        console.log('Ответ сервера на назначение:', response.data);
        
        // Обновляем чат в хранилище
        store.dispatch('updateChat', response.data);
        
        // Устанавливаем текущий чат
        store.commit('setCurrentChat', response.data);
        
        // Запрашиваем обновление списка чатов
        socket.emit('request_chats_update');
      } catch (err) {
        console.error('Ошибка при назначении чата:', err);
        
        if (err.response && err.response.data && err.response.data.error) {
          alert(`Ошибка: ${err.response.data.error}`);
        } else {
          alert('Не удалось назначить чат');
        }
      }
    };
    
    // Обработчик обновления чата
    const handleChatUpdate = (updatedChat) => {
      if (!updatedChat || !updatedChat._id) {
        console.error('ChatView: Получен некорректный объект чата:', updatedChat);
        return;
      }
      
      if (updatedChat._id === chatId.value) {
        console.log('Получено обновление текущего чата:', updatedChat._id);
        
        // Обновляем чат в хранилище
        store.dispatch('updateChatSilently', updatedChat);
        
        // Принудительно обновляем локальное состояние
        store.commit('setCurrentChat', updatedChat);
        
        // Обновляем статусы, если они изменились
        if (updatedChat.status && (!currentChat.value.status || 
            updatedChat.status._id !== currentChat.value.status._id)) {
          console.log('Статус чата изменился, обновляем UI');
        }
      }
    };
    
    // Обработчик нового сообщения
    const handleNewMessage = (message) => {
      if (!message || !message._id) {
        console.error('ChatView: Получен некорректный объект сообщения:', message);
        return;
      }
      
      if (message.chat === chatId.value) {
        console.log('Получено новое сообщение для текущего чата:', message._id);
        
        // Добавляем сообщение в список
        messages.value.push(message);
        
        // Прокручиваем к новому сообщению
        nextTick(() => {
          scrollToBottom();
        });
      }
    };
    
    // Функция для загрузки предыдущих сообщений
    const loadMoreMessages = () => {
      if (hasMoreMessages.value && !loadingMore.value) {
        page.value++;
        fetchMessages(true);
      }
    };
    
    // Добавляем обработчик скролла
    const handleScroll = () => {
      if (!messagesContainer.value) return;
      
      // Если пользователь прокрутил до верха и есть еще сообщения, загружаем их
      if (messagesContainer.value.scrollTop < 50 && hasMoreMessages.value && !loadingMore.value) {
        // Запоминаем текущую высоту прокрутки и высоту контента
        const scrollHeight = messagesContainer.value.scrollHeight;
        
        loadMoreMessages();
        
        // После загрузки новых сообщений восстанавливаем позицию прокрутки
        nextTick(() => {
          if (messagesContainer.value) {
            const newScrollHeight = messagesContainer.value.scrollHeight;
            messagesContainer.value.scrollTop = newScrollHeight - scrollHeight;
          }
        });
      }
    };
    
    // Улучшаем функцию для отображения содержимого сообщения
    const getMessageContent = (message) => {
      // Проверяем, является ли сообщение медиа-сообщением
      if (!isMediaMessage(message)) {
        return message.text;
      }
      
      // Проверяем наличие URL медиафайла
      let mediaUrl = message.mediaUrl || (message.media && message.media.url);
      
      // Если URL отсутствует, возвращаем только текст
      if (!mediaUrl) {
        return message.text;
      }
      
      // Добавляем базовый URL API, если URL начинается с /
      if (mediaUrl.startsWith('/')) {
        const apiUrl = (process.env.VUE_APP_API_URL + '/api') || '';
        mediaUrl = `${apiUrl}${mediaUrl}`;
      }
      
      const mediaType = message.media ? message.media.type : null;
      
      // Для медиа-сообщений добавляем иконку и стилизацию
      let icon = '';
      let mediaContent = '';
      
      // Определяем тип медиа и соответствующую иконку
      if (mediaType) {
        switch (mediaType) {
          case 'photo':
            icon = '📷';
            if (mediaUrl) {
              mediaContent = `<div class="media-preview"><img src="${mediaUrl}" alt="Фото" class="media-image" onclick="window.open('${mediaUrl}', '_blank')"></div>`;
            }
            break;
          case 'video':
            icon = '🎥';
            if (mediaUrl) {
              // Проверяем, является ли это видеосообщением (по тексту или другим признакам)
              const isRoundVideo = message.text.includes('[Видеосообщение]');
              
              if (isRoundVideo) {
                // Отображаем как круглое видеосообщение
                mediaContent = `
                  <div class="media-preview">
                    <video src="${mediaUrl}" controls loop class="media-video round-video"></video>
                  </div>`;
              } else {
                // Отображаем как обычное видео
                mediaContent = `<div class="media-preview"><video src="${mediaUrl}" controls class="media-video"></video></div>`;
              }
            }
            break;
          case 'audio':
            icon = '🎵';
            if (mediaUrl) {
              mediaContent = `
                <div class="media-preview">
                  <audio controls class="media-audio">
                    <source src="${mediaUrl}" type="audio/mpeg">
                    <source src="${mediaUrl}" type="audio/ogg">
                    <source src="${mediaUrl}" type="audio/wav">
                    Ваш браузер не поддерживает аудио элемент.
                  </audio>
                </div>`;
            }
            break;
          case 'voice':
            icon = '🎤';
            if (mediaUrl) {
              mediaContent = `
                <div class="media-preview">
                  <audio controls class="media-audio">
                    <source src="${mediaUrl}" type="audio/ogg">
                    <source src="${mediaUrl}" type="audio/mpeg">
                    <source src="${mediaUrl}" type="audio/wav">
                    Ваш браузер не поддерживает аудио элемент.
                  </audio>
                </div>`;
            }
            break;
          case 'document':
            icon = '📄';
            if (mediaUrl) {
              // Для PDF и других документов используем встроенный просмотрщик или ссылку
              const fileName = message.media.fileName || 'документ';
              mediaContent = `
                <div class="media-preview">
                  <a href="${mediaUrl}" target="_blank" class="media-document">
                    <i class="fas fa-file-download"></i> Открыть ${fileName}
                  </a>
                </div>`;
            }
            break;
          default:
            icon = '📎';
            if (mediaUrl) {
              mediaContent = `
                <div class="media-preview">
                  <a href="${mediaUrl}" target="_blank" class="media-document">
                    <i class="fas fa-file-download"></i> Открыть файл
                  </a>
                </div>`;
            }
        }
      } else if (message.mediaType) {
        // Обрабатываем устаревший формат медиа
        icon = '📎';
        if (mediaUrl) {
          mediaContent = `<div class="media-preview"><a href="${mediaUrl}" target="_blank" class="media-document"><i class="fas fa-file-download"></i> Открыть файл</a></div>`;
        }
      }
      
      // Возвращаем стилизованное сообщение с иконкой и превью медиа
      return `${mediaContent}<span class="media-icon">${icon}</span> ${message.text}`;
    };
    
    // Функция для отметки сообщений как прочитанных
    const markAsRead = async () => {
      if (!chatId.value || loading.value) return;
      
      try {
        // Проверяем, есть ли непрочитанные сообщения
        const hasUnread = messages.value.some(msg => !msg.isRead && !msg.fromMe);
        
        if (hasUnread) {
          console.log('Отмечаем сообщения как прочитанные');
          
          // Отправляем запрос на сервер
          await axios.post(`/telegram/chats/${chatId.value}/read`);
          
          // Обновляем локальное состояние
          messages.value = messages.value.map(msg => ({
            ...msg,
            isRead: true
          }));
          
          // Обновляем счетчик непрочитанных сообщений в чате
          if (currentChat.value) {
            store.commit('updateChatUnreadCount', {
              chatId: chatId.value,
              unreadCount: 0
            });
          }
        }
      } catch (error) {
        console.error('Ошибка при отметке сообщений как прочитанных:', error);
        
        // Если ошибка связана с доступом, показываем сообщение
        if (error.response && error.response.status === 403) {
          // Не показываем ошибку, просто логируем
          console.warn('Нет прав для отметки сообщений как прочитанных:', error.response.data.error);
        }
      }
    };
    
    // Функция для форматирования даты
    const formatDate = (date) => {
      const messageDate = new Date(date);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Проверяем, сегодня ли это
      if (messageDate.toDateString() === today.toDateString()) {
        return 'Сегодня';
      }
      
      // Проверяем, вчера ли это
      if (messageDate.toDateString() === yesterday.toDateString()) {
        return 'Вчера';
      }
      
      // Для других дат используем полный формат
      const options = { day: 'numeric', month: 'long', year: 'numeric' };
      return messageDate.toLocaleDateString('ru-RU', options);
    };
    
    // Вычисляемое свойство для сообщений с разделителями дат
    const messagesWithDateSeparators = computed(() => {
      if (!messages.value.length) return [];
      
      const result = [];
      let currentDate = null;
      
      messages.value.forEach(message => {
        const messageDate = new Date(message.date);
        const dateString = messageDate.toDateString();
        
        // Если дата изменилась, добавляем разделитель
        if (dateString !== currentDate) {
          currentDate = dateString;
          result.push({
            separator: true,
            date: formatDate(message.date)
          });
        }
        
        // Добавляем сообщение
        result.push(message);
      });
      
      return result;
    });
    
    // Улучшаем функцию для определения, является ли сообщение медиа-сообщением
    const isMediaMessage = (message) => {
      // Проверяем наличие медиа в сообщении
      if (message.mediaType && message.mediaType !== 'null') {
        return true;
      }
      
      // Проверяем наличие медиа-объекта
      if (message.media && message.media.type && message.media.type !== 'null') {
        return true;
      }
      
      // Проверяем наличие URL медиа
      if (message.mediaUrl && message.mediaUrl !== 'null') {
        return true;
      }
      
      return false;
    };
    
    // Следим за изменением ID чата
    watch(() => props.id, (newId) => {
      chatId.value = newId;
      fetchChat();
      fetchMessages();
    });
    
    // Добавляем наблюдатель за изменением списка сообщений
    watch(() => messages.value.length, () => {
      scrollToBottom();
    });
    
    onMounted(async () => {
      console.log('ChatView: компонент смонтирован');
      
      // Загружаем статусы
      await fetchStatuses();
      
      // Подписываемся на обновления чата
      subscribeToChat(chatId.value);
      
      // Загружаем сообщения чата
      fetchMessages();
      
      // Подписываемся на новые сообщения
      socket.on('new_message', handleNewMessage);
      
      // Подписываемся на обновления чата
      socket.on('chat_updated', handleChatUpdate);
      
      // Добавляем обработчик скролла
      if (messagesContainer.value) {
        messagesContainer.value.addEventListener('scroll', handleScroll);
      }
      
      // Отмечаем сообщения как прочитанные при открытии чата
      setTimeout(markAsRead, 1000);
    });
    
    onUnmounted(() => {
      console.log('ChatView: компонент размонтирован');
      
      // Отписываемся от обновлений чата
      unsubscribeFromChat(chatId.value);
      
      // Отписываемся от новых сообщений
      socket.off('new_message', handleNewMessage);
      
      // Отписываемся от обновлений чата
      socket.off('chat_updated', handleChatUpdate);
      
      // Удаляем обработчик скролла
      if (messagesContainer.value) {
        messagesContainer.value.removeEventListener('scroll', handleScroll);
      }
    });
    
    return {
      chat: currentChat,
      messages,
      newMessage,
      sending,
      loading,
      error,
      messagesContainer,
      statuses,
      statusDropdownOpen,
      chatName,
      statusName,
      statusColor,
      isAssignedToMe,
      isAssignedToOther,
      assignButtonText,
      assignButtonIcon,
      assignButtonTitle,
      sendMessage,
      changeStatus,
      assignToMe,
      formatTime,
      getInitials,
      toggleStatusDropdown,
      closeStatusDropdown,
      canSendMessages,
      sendButtonTitle,
      getMessageContent,
      isDevelopment,
      markAsRead,
      formatDate,
      messagesWithDateSeparators,
      isMediaMessage,
    };
  }
};
</script>

<style scoped>
.chat-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.chat-info {
  display: flex;
  align-items: center;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
  overflow: hidden;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background-color: #e0e0e0;
  color: #757575;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-details {
  display: flex;
  flex-direction: column;
}

.chat-name {
  font-weight: bold;
  font-size: 16px;
}

.chat-meta {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #757575;
}

.chat-status-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-right: 8px;
}

.chat-actions {
  display: flex;
  align-items: center;
}

.status-dropdown {
  position: relative;
  margin-right: 10px;
}

.status-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.status-button i {
  margin-left: 5px;
}

.status-dropdown-content {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  z-index: 1;
  border-radius: 4px;
  margin-top: 5px;
}

.status-item {
  padding: 8px 12px;
  cursor: pointer;
  border-left: 4px solid transparent;
  transition: background-color 0.2s;
}

.status-item:hover {
  background-color: #f5f5f5;
}

.assign-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background-color: #e0e0e0;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
}

.assign-button i {
  margin-right: 5px;
}

.assign-button:hover {
  background-color: #d0d0d0;
}

.assign-button.assigned {
  background-color: #4caf50;
  color: white;
}

.assign-button.assigned:hover {
  background-color: #43a047;
}

.assign-button.assigned-other {
  background-color: #9e9e9e;
  color: white;
  cursor: not-allowed;
}

.assign-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
}

.loading-messages, .error-message, .no-messages {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #757575;
}

.messages {
  display: flex;
  flex-direction: column;
}

.message {
  max-width: 70%;
  margin-bottom: 10px;
  align-self: flex-start;
}

.message.from-me {
  align-self: flex-end;
}

.message-content {
  padding: 10px;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message.from-me .message-content {
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
}

.message-text {
  font-size: 14px;
  margin-bottom: 5px;
  word-break: break-word;
}

.message-time {
  font-size: 11px;
  color: #707579;
  text-align: right;
}

.message-input-container {
  display: flex;
  padding: 10px;
  background-color: #ffffff;
  border-top: 1px solid #e0e0e0;
}

.message-input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  resize: none;
  max-height: 100px;
  font-size: 14px;
  margin-right: 10px;
}

.send-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #2196f3;
  color: white;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.send-button:hover {
  background-color: #1976d2;
}

.send-button:disabled {
  background-color: #90caf9;
  cursor: not-allowed;
}

.chat-error-message {
  background-color: #ffebee;
  color: #d32f2f;
  padding: 15px;
  text-align: center;
  font-size: 14px;
  border-radius: 5px;
  margin: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chat-locked-message {
  background-color: #fff3e0;
  color: #e65100;
  padding: 10px;
  text-align: center;
  font-size: 14px;
  border-top: 1px solid #ffe0b2;
}

.chat-locked-message i {
  margin-right: 5px;
}

.message-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.media-message .message-content {
  background-color: #ffffff;
}

.message.from-me.media-message .message-content {
  background-color: #e3f2fd;
}

.media-icon {
  font-size: 1.2em;
  margin-right: 5px;
  vertical-align: middle;
}

.message-text {
  font-size: 14px;
  margin-bottom: 5px;
  word-break: break-word;
}

.media-preview {
  margin-bottom: 10px;
  max-width: 100%;
  border-radius: 5px;
  overflow: hidden;
  display: flex;
  justify-content: center;
}

.media-image {
  max-width: 100%;
  max-height: 300px;
  display: block;
}

.media-video {
  max-width: 100%;
  max-height: 300px;
  display: block;
}

.media-audio {
  width: 100%;
  min-width: 250px;
  display: block;
  border-radius: 20px;
  background-color: #f5f5f5;
}

.media-document {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #2196f3;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.3s;
}

.media-document i {
  margin-right: 8px;
}

.media-document:hover {
  background-color: #1976d2;
  text-decoration: none;
  color: white;
}

.round-video {
  width: 240px;
  height: 240px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #000;
}

/* Стили для разделителя даты */
.date-separator {
  display: flex;
  align-items: center;
  margin: 20px 0;
  width: 100%;
}

.date-separator-line {
  flex: 1;
  height: 1px;
  background-color: #e0e0e0;
}

.date-separator-text {
  padding: 0 10px;
  color: #757575;
  font-size: 12px;
  white-space: nowrap;
}
</style> 